// Copyright (c) 2026. CYBERKOALA.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

/**
 * Shows a browser notification using the correct API for the environment.
 * On Android Chrome, `new Notification()` is illegal — must use
 * `ServiceWorkerRegistration.showNotification()` instead.
 */
export async function showNotification(
  title: string,
  options?: NotificationOptions
): Promise<void> {
  // ============================================================
  // ELECTRON
  // ============================================================
  // @ts-ignore
  if (window.electronAPI && typeof window.electronAPI.showNativeNotification === 'function') {
    try {
      // @ts-ignore
      window.electronAPI.showNativeNotification(title, {
        body: options?.body,
        silent: options?.silent,
      });

      return;
    } catch (error) {
      console.error('[Notifications] Electron notification failed:', error);
    }
  }

  // Browser / PWA / Android
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;

      await registration.showNotification(title, options);

      return;
    } catch (error) {
      console.warn('[Notifications] Service Worker notification failed:', error);
    }
  }

  // Desktop browser fallback
  if (!('Notification' in window)) {
    console.warn('[Notifications] Notifications are not supported');
    return;
  }

  try {
    if (Notification.permission === 'granted') {
      new Notification(title, options);
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        new Notification(title, options);
      }
    }
  } catch (error) {
    console.error('[Notifications] Browser notification failed:', error);
  }
}
